import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { BaseDatasource } from '@shared/base/base.datasource';
import { BaseResponse } from '@shared/base/base-response';
import { TableColumn } from '@shared/interface/table.interface';
import { BottomTableComponent } from '@shared/components/bottom-table/bottom-table.component';
import { CardEmptyComponent } from '@shared/components/card';
import { SectionLoaderComponent } from '@shared/components/loader';
import { ModalFormComponent } from '@shared/components/modal/modal-form/modal-form.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { TableContentComponent } from '@shared/components/table-content/table-content.component';
import { TargetDistributorService } from '../../../target-distributor.service';
import { ITargetDistributorMonitoring__Invoice } from '../../../target-distributor-monitoring.interface';
import { createMonitoringPaginatorIntl } from '../../monitoring-paginator-intl';
import { MONITORING_PAGINATOR_ROUTE_PROVIDERS, MonitoringPaginatorRoute } from '../../monitoring-paginator-route';

/** Payload berbentuk objek, mengikuti `IModalDetailDistributorPayload`. */
export interface IModalNilaiInvoicePayload {
  targetDistributorId: string;
  distributorId: string;
  distributorName: string;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Status SO yang dipakai saat membuka halaman Detail SO dari kolom Action.
 *
 * Response `invoice-list` tidak memuat status SO, sementara route detail SO berbentuk
 * `/sales-order/{status}/{id}`. Aman memakai `history` karena AC-01 membatasi baris di
 * tabel ini pada SO berstatus Selesai / Selesai Sebagian — keduanya masuk halaman History.
 */
const SO_DETAIL_STATUS_SEGMENT = 'history';

/**
 * [MAI-2604] Sub-modal "Nilai Invoice [Nama Distributor]".
 *
 * Dibuka dari link "Lihat Detail" pada kartu Total Nilai Invoice (SO) di modal Detail per
 * Distributor. Modal ini MENGGANTIKAN modal induk di layar (QA 28 Agu 2026): induknya
 * disembunyikan selagi modal ini terbuka lalu ditampilkan lagi lewat event `closed`,
 * sehingga tombol X tetap mengembalikan pengguna ke modal Detail per Distributor (AC-01).
 *
 * Nilai QTY, berat, dan nominal datang sudah terformat dari backend dan hanya mencerminkan
 * produk relevan sesuai setting program — tidak ada formatting maupun pengurutan di frontend
 * (endpoint tidak punya param `sort`).
 */
@Component({
  selector: 'app-modal-nilai-invoice',
  standalone: true,
  imports: [CommonModule, MatTableModule, InlineSVGModule, ModalFormComponent, SectionLoaderComponent, CardEmptyComponent, TableContentComponent, BottomTableComponent],
  providers: [
    // Paginator memakai `ActivatedRoute` lokal supaya indeks halamannya tidak ikut
    // query param `page` milik tabel distributor di halaman induk.
    ...MONITORING_PAGINATOR_ROUTE_PROVIDERS,
    // Instance sendiri supaya label tidak bergantung pada `MatPaginatorIntl` global.
    { provide: MatPaginatorIntl, useFactory: createMonitoringPaginatorIntl },
  ],
  templateUrl: './modal-nilai-invoice.component.html',
  styleUrls: ['./modal-nilai-invoice.component.scss'],
})
export class ModalNilaiInvoiceComponent {
  @ViewChild('modal') private modal!: ModalFormComponent;

  /**
   * Modal ditutup (X atau backdrop) — pemanggil memakai ini untuk menampilkan kembali
   * modal induk yang disembunyikan saat modal ini dibuka.
   */
  @Output() closed = new EventEmitter<void>();

  readonly STRING_CONSTANTS = STRING_CONSTANTS;
  readonly dataSource = new BaseDatasource<ITargetDistributorMonitoring__Invoice>();
  readonly tableColumns: TableColumn[] = [
    { key: 'so_code', title: 'SO ID', isSortable: false },
    { key: 'total_qty', title: 'Total QTY', isSortable: false },
    { key: 'invoice_code', title: 'Invoice ID', isSortable: false },
    { key: 'total_invoice', title: 'Nilai Invoice', isSortable: false },
    { key: 'actions', title: 'Action', isSortable: false },
  ];
  readonly displayedColumns = this.tableColumns.map((column) => column.key);

  payload: IModalNilaiInvoicePayload | null = null;
  /** Loader penuh hanya untuk muat pertama; perpindahan halaman memakai skeleton per sel. */
  isFirstLoad = true;
  hasError = false;

  /** `showFooter: false` — design tidak punya tombol aksi di bawah modal. */
  modalConfig: ModalConfig = {
    modalTitle: '',
    showHeader: true,
    showFooter: false,
  };

  /**
   * Lebar 1000px (design node 8212-42573) diatur lewat `windowClass` di file scss.
   *
   * Tidak perlu menaikkan z-index terhadap modal induk: induknya disembunyikan selama
   * modal ini terbuka, jadi keduanya tidak pernah tampil bersamaan.
   */
  readonly modalOptions: NgbModalOptions = {
    windowClass: 'modal-nilai-invoice',
    scrollable: true,
  };

  constructor(
    private readonly targetDistributorService: TargetDistributorService,
    private readonly paginatorRoute: MonitoringPaginatorRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    this.dataSource.pageSizeSelect = [10, 25, 50];
    this.dataSource.pageSize = DEFAULT_PAGE_SIZE;
  }

  /**
   * `detectChanges()` sebelum `open()` wajib: NgbModal baru meng-instantiate `<ng-template>`
   * saat `open()` dipanggil, sehingga judul harus sudah ter-render lebih dulu.
   */
  open(payload: IModalNilaiInvoicePayload): void {
    this.payload = payload;
    this.hasError = false;
    this.isFirstLoad = true;
    this.dataSource.current_page = 0;
    this.dataSource.pageSize = DEFAULT_PAGE_SIZE;
    // Paginator ikut dikembalikan ke halaman pertama; kalau tidak, membuka modal untuk
    // distributor lain akan menyorot halaman terakhir yang dipakai sebelumnya.
    this.paginatorRoute.setPage(0);
    this.modalConfig = { ...this.modalConfig, modalTitle: `Nilai Invoice ${payload.distributorName}` };
    // NgbModal menandai modal induk `aria-hidden` begitu sub-modal terbuka. Kalau tombol
    // pemicu ("Lihat Detail") masih ter-fokus, browser menolak aria-hidden itu dan mencatat
    // warning a11y — jadi fokusnya dilepas dulu.
    (document.activeElement as HTMLElement | null)?.blur();
    this.cdr.detectChanges();
    // `open()` resolve saat modal ditutup/di-dismiss — dipakai untuk memunculkan kembali
    // modal induk, termasuk saat modal ditutup bukan lewat method `close()` di bawah.
    this.modal.open().then(() => this.closed.emit());
    this.fetchInvoices();
  }

  close(): void {
    this.modal.close();
  }

  get isEmpty(): boolean {
    return this.dataSource.isTableLoaded && !this.dataSource.hasItems;
  }

  /**
   * [AC-02 MAI-1363 · request Dinda 03-09-2026] Satu Invoice ID per baris.
   *
   * Backend mengirim seluruh Invoice ID milik 1 SO sebagai satu string dipisahkan koma
   * (retur bisa memicu action di beberapa invoice untuk SO yang sama). Ditampilkan
   * sebaris-sebaris supaya kolom tidak memanjang; komanya tetap ikut, kecuali di baris
   * terakhir.
   */
  invoiceCodes(row: ITargetDistributorMonitoring__Invoice): string[] {
    return (row.invoice_code ?? '')
      .split(',')
      .map((code) => code.trim())
      .filter((code) => code.length > 0);
  }

  onPageChange(source: BaseDatasource<ITargetDistributorMonitoring__Invoice>): void {
    this.dataSource.pageSize = source.pageSize;
    this.goToPage(source.current_page);
  }

  private goToPage(page: number): void {
    this.dataSource.current_page = page;
    // Paginator membaca halaman dari route lokal ini, bukan dari URL halaman induk.
    this.paginatorRoute.setPage(page);
    this.fetchInvoices();
  }

  /**
   * [AC-01] Ikon Action membuka halaman Detail SO. Dibuka di tab baru supaya modal yang
   * sedang terbuka (beserta modal induk yang tersembunyi di belakangnya) tidak hilang —
   * pola yang sama dipakai `table-product-promag-list` saat membuka detail program dari
   * dalam detail SO.
   */
  onDetailSo(row: ITargetDistributorMonitoring__Invoice): void {
    if (!row.so_id) return;

    const url = this.router.serializeUrl(this.router.createUrlTree([`/sales-order/${SO_DETAIL_STATUS_SEGMENT}/${row.so_id}`]));
    window.open(url, '_blank');
  }

  private fetchInvoices(): void {
    if (!this.payload) return;

    this.dataSource.isFinishLoadingSubject.next(false);

    this.targetDistributorService
      .getMonitoringInvoiceList(this.payload.targetDistributorId, this.payload.distributorId, {
        page: this.dataSource.current_page,
        size: this.dataSource.pageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.applyInvoices(res),
        error: () => this.applyInvoices(null),
      });
  }

  private applyInvoices(res: BaseResponse<ITargetDistributorMonitoring__Invoice[]> | null): void {
    const rows = res?.data ?? [];

    // Gagal saat muat pertama = tidak ada apa pun untuk ditampilkan; kegagalan saat pindah
    // halaman cukup jatuh ke empty state supaya tabel tidak ikut hilang.
    this.hasError = !res && this.isFirstLoad;

    this.dataSource.tableSubject.next(rows);
    this.dataSource.totalItem = res?.total_data ?? 0;
    this.dataSource.totalPage = res?.total_page ?? 0;
    this.dataSource.current_page = res?.current_page ?? 0;
    this.dataSource.hasItems = rows.length > 0;
    this.dataSource.isFinishLoadingSubject.next(true);
    this.isFirstLoad = false;
    this.cdr.detectChanges();
  }
}
